<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { onMounted, ref } from 'vue'
import { faMinus, faPlus } from '@fortawesome/duotone-regular-svg-icons'
import { GRADE_ADULT, GRADE_PREP, gradeTypeMapping } from '~/utils/gradeSorter'
import AuCheckbox from '~/components/ui/AuCheckbox.vue'
import AggregationButton from '~/components/search/AggregationButton.vue'

interface GradeKeyEntry {
  key: string
  count: number
}

interface GradeAggregationEntry {
  group: string
  keys: GradeKeyEntry[]
}

const props = defineProps<{
  title: string
  isRefetching: boolean
  criterions: string[]
  aggregations: GradeAggregationEntry[]
  translator: (entry: GradeKeyEntry) => string
  openOnMount?: boolean
}>()

const emit = defineEmits(['toggleCriterion'])

const { t } = useI18n()
const open = ref(false)

const isGroupChecked = (group: string) => {
  const keys = gradeTypeMapping[group] ?? []
  return keys.length > 0 && keys.every(key => props.criterions.includes(key))
}

const handleGroupToggle = (group: string) => {
  const keys = gradeTypeMapping[group] ?? []
  const allChecked = isGroupChecked(group)

  keys.forEach((key) => {
    if (allChecked || !props.criterions.includes(key)) {
      emit('toggleCriterion', key)
    }
  })
}

const showConnectedGrades = (entry: GradeAggregationEntry) => {
  return entry.keys?.some(k => ![GRADE_ADULT, GRADE_PREP].includes(k.key))
}

onMounted(() => {
  if (props.openOnMount || props.criterions.length > 0) {
    open.value = true
  }
})
</script>

<template>
  <div class="rounded-xl bg-white">
    <button
      class="group flex w-full cursor-pointer items-center justify-between rounded px-4 py-3 focus-visible:ring"
      type="button"
      @click="open = !open"
    >
      <span
        class="font-bold underline-offset-4 group-hover:underline"
        v-text="title"
      />
      <font-awesome-icon
        :icon="open ? faMinus : faPlus"
        :spin="open"
        style="--fa-animation-duration: 200ms; --fa-animation-iteration-count: 1;"
      />
    </button>
    <Transition
      :duration="500"
      name="nested"
    >
      <div
        v-show="open"
        class="outer border-t-gray-10 mx-4 border-t py-3"
      >
        <ul class="inner flex flex-col gap-3">
          <li
            v-for="entry in aggregations"
            :key="entry.group"
          >
            <AggregationButton
              :name="t(`search.filterBoxes.${entry.group}`)"
              :disabled="isRefetching"
              type="label"
              class="font-bold"
              full-width
            >
              <AuCheckbox
                :id="entry.group"
                :model-value="isGroupChecked(entry.group)"
                :disabled="isRefetching"
                @update:model-value="handleGroupToggle(entry.group)"
              />
            </AggregationButton>

            <ul
              v-if="showConnectedGrades(entry)"
              class="ml-2"
            >
              <li
                v-for="subKey in entry.keys"
                :key="subKey.key"
              >
                <AggregationButton
                  :name="translator(subKey)"
                  :entry="subKey"
                  type="label"
                  :disabled="isRefetching"
                >
                  <AuCheckbox
                    :id="subKey.key"
                    :model-value="criterions.includes(subKey.key)"
                    :disabled="isRefetching"
                    @update:model-value="emit('toggleCriterion', subKey.key)"
                  />
                </AggregationButton>
              </li>
            </ul>
          </li>
        </ul>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.nested-enter-active,
.nested-leave-active {
  transition: all 0.3s linear;
  max-height: 1000px;
}
.nested-leave-active {
  transition-delay: 0.2s;
}
.nested-enter-from,
.nested-leave-to {
  transform: translateY(-5px);
  opacity: 0;
  max-height: 0;
}
.nested-enter-active .inner,
.nested-leave-active .inner {
  transition:
    transform 0.3s ease-in-out,
    opacity 0.3s ease-in-out;
  will-change: transform, opacity;
}
.nested-enter-active .inner {
  transition-delay: 0.2s;
}
.nested-enter-from .inner {
  transform: translateY(-5px);
  opacity: 0.001;
}
.nested-leave-to .inner {
  transform: translateY(-5px);
  opacity: 0.001;
}
</style>
